import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  AlertOctagon,
  Sparkles,
  MessageSquare,
  Send,
  UserCheck,
  HelpCircle,
  Activity as ActivityIcon,
  X,
  Inbox as InboxIconSmall,
  Target as TargetIconSmall,
  User as UserIconSmall,
  CheckSquare,
  FolderPlus,
  Archive,
} from "lucide-react";
import { ChannelIcon } from "@/components/BrandIcons";
import { ActionPopover } from "@/components/ActionPopover";
import { PersonLink } from "@/components/PersonLink";
import { usePersonPanel } from "@/context/PersonPanelContext";
import { relTime, gravatarUrl, initials } from "@/lib/utils-fos";
import { ReachOutModal } from "@/pages/Inbox";

// Activity page — the "what's happening" surface of the app.
// Two stacked panels:
//   1. Escalations triage (top, expanded when non-empty)
//   2. Chronological feed of events grouped by person, with quick reach-out actions
export default function Activity() {
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const [tab, setTab] = useState("all");
  const [events, setEvents] = useState([]);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [q, setQ] = useState("");
  const [expandedEscId, setExpandedEscId] = useState(null);

  const load = async () => {
    const [ev, pp, pr, es] = await Promise.all([
      endpoints.events(), endpoints.people(), endpoints.projects(), endpoints.escalations(),
    ]);
    setEvents(ev); setPeople(pp); setProjects(pr); setEscalations(es);
  };
  useEffect(() => { load(); }, []);

  // Auto-scroll & highlight for cmd+K navigations
  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-focus-id="${focus}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlighted-entity");
      }
    }, 300);
    return () => clearTimeout(t);
  }, [focus, events.length, escalations.length]);

  const pendingEsc = useMemo(() => escalations.filter((e) => e.status === "pending"), [escalations]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let out = events;
    if (tab === "critical") out = out.filter((e) => (e.urgency_score || 0) >= 4);
    else if (tab === "waiting") out = out.filter((e) => e.classification === "request");
    else if (tab === "commitments") out = out.filter((e) => e.classification === "commitment");
    if (query) {
      out = out.filter((e) =>
        e.content_summary?.toLowerCase().includes(query) ||
        e.source_channel?.toLowerCase().includes(query)
      );
    }
    return [...out].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [events, tab, q]);

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl mx-auto">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Activity</h1>
          <p className="text-sm text-neutral-500 mt-1">Everything happening across your workspace — triage escalations and take action.</p>
        </div>
      </div>

      {/* Escalation triage */}
      <section className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <AlertOctagon size={14} className={pendingEsc.length ? "text-[#FF453A]" : "text-neutral-400"} />
          <h2 className="text-sm font-semibold">Escalation triage</h2>
          <span className="text-xs text-neutral-500">{pendingEsc.length} pending</span>
        </div>
        {pendingEsc.length === 0 ? (
          <div className="fos-card p-6 text-center">
            <p className="text-sm text-neutral-500">No pending escalations. You&apos;re on top of things.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {pendingEsc.map((esc) => (
              <EscalationRow
                key={esc.id}
                esc={esc}
                people={people}
                projects={projects}
                onChange={load}
                focused={focus === esc.id}
                expanded={expandedEscId === esc.id}
                onToggle={() => setExpandedEscId((prev) => (prev === esc.id ? null : esc.id))}
              />
            ))}
          </div>
        )}
      </section>

      {/* Activity feed */}
      <section className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <div className="flex items-center gap-2">
            <ActivityIcon size={14} className="text-neutral-500" />
            <h2 className="text-sm font-semibold">Recent activity</h2>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {["all", "critical", "waiting", "commitments"].map((k) => (
              <button key={k} data-active={tab === k} onClick={() => setTab(k)} className="chip capitalize" data-testid={`activity-filter-${k}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[180px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
            <Search size={12} className="text-neutral-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search activity…"
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="activity-search"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="fos-card p-6 text-center">
            <p className="text-sm text-neutral-500 mb-2">No activity yet.</p>
            <p className="text-xs text-neutral-400">Once you connect Gmail or Slack in Settings, messages will appear here.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {filtered.slice(0, 60).map((e) => (
              <EventRow key={e.id} event={e} people={people} projects={projects} focused={focus === e.id} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Escalation triage row — 4 actions:
//  Handle myself · Escalate to founder (with AI brief) · Reach out to person · Need more info
// -----------------------------------------------------------------------------
function EscalationRow({ esc, people, projects, onChange, focused, expanded, onToggle }) {
  const nav = useNavigate();
  const [briefLoading, setBriefLoading] = useState(false);
  const [reachOutOpen, setReachOutOpen] = useState(false);
  const [reachOutInitial, setReachOutInitial] = useState(null);

  useEffect(() => { if (focused && !expanded) onToggle?.(); }, [focused]);

  const linkedPeople = (esc.people_ids || []).map((id) => people.find((p) => p.id === id)).filter(Boolean);
  const linkedProjects = (esc.project_ids || []).map((id) => projects.find((p) => p.id === id)).filter(Boolean);

  const setStatus = async (status, note) => {
    await endpoints.updateEscalation(esc.id, { status });
    if (status === "resolved") toast.success("Marked as handled");
    else if (status === "dismissed") toast.success(note || "Escalation dismissed");
    onChange();
  };

  // "Escalate to founder" → drafts an AI brief and pre-fills the ReachOutModal
  // targeted at the founder. Sending saves to Sent; cancelling saves to Drafts.
  const openEscalateToFounder = async () => {
    setBriefLoading(true);
    let body = `Jon,\n\nEscalation: ${esc.reason}\n\n${esc.context_summary || ""}\n\nRisk if we do nothing: ${esc.risk || "—"}\n\nWhat I'd like to do next: <fill in>`;
    try {
      const r = await endpoints.escalationBrief(esc.id);
      if (r.brief) body = r.brief;
    } catch (e) { /* fall through with default body */ }
    setBriefLoading(false);
    const founder = people.find((p) => (p.relationship_type || "").toLowerCase() === "founder")
      || people.find((p) => (p.role || "").toLowerCase().includes("founder"))
      || people.find((p) => (p.segment || "").toLowerCase() === "founder");
    setReachOutInitial({
      platform: "email",
      to_person_id: founder?.id || "",
      to_display: founder?.email || "",
      subject: `Escalation: ${esc.reason}`,
      body,
      related_escalation_id: esc.id,
    });
    setReachOutOpen(true);
  };

  const openReachOut = () => {
    const targetPerson = linkedPeople[0];
    setReachOutInitial({
      platform: (targetPerson?.preferred_channel || "email").toLowerCase(),
      to_person_id: targetPerson?.id || "",
      to_display: targetPerson?.email || "",
      subject: `Re: ${esc.reason}`,
      body: "",
      related_escalation_id: esc.id,
    });
    setReachOutOpen(true);
  };

  // "Create as task" — drop the escalation into the first linked project (or
  // navigate the user to the Tasks page to pick a scope) as a critical task.
  const createAsTask = async () => {
    const projectId = linkedProjects[0]?.id;
    if (!projectId) {
      toast.info("This escalation isn't linked to a project. Pick a scope on the Tasks page.");
      nav("/projects");
      return;
    }
    try {
      await endpoints.createTask({
        title: esc.reason,
        project_id: projectId,
        priority: "critical",
        status: "todo",
        notes: esc.context_summary,
      });
      toast.success(`Task added to ${linkedProjects[0].name}`);
      setStatus("resolved");
    } catch (e) { toast.error("Could not create task"); }
  };

  return (
    <div
      className={`fos-card p-4 ${focused ? "highlighted-entity" : ""}`}
      data-testid={`escalation-${esc.id}`}
      data-focus-id={esc.id}
    >
      <button onClick={onToggle} className="w-full text-left">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900">{esc.reason}</p>
            {esc.risk && <p className="text-xs text-[#F97316] mt-0.5">{esc.risk}</p>}
            {esc.context_summary && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{esc.context_summary}</p>}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-50 text-[#FF453A]">Urgency {esc.urgency}/5</span>
            <span className="text-[10px] text-neutral-500">{relTime(esc.created_at)}</span>
          </div>
        </div>
        {(linkedProjects.length > 0 || linkedPeople.length > 0) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {linkedProjects.map((p) => (
              <span key={p.id} className="text-[11px] px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">{p.name}</span>
            ))}
            {linkedPeople.map((p) => (
              <span key={p.id} className="text-[11px] px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">@{p.name}</span>
            ))}
          </div>
        )}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-neutral-200 space-y-3 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <ActionButton
              icon={<CheckSquare size={13} />}
              label="Create as task"
              onClick={createAsTask}
              testid={`triage-create-task-${esc.id}`}
            />
            <ActionButton
              icon={briefLoading ? <Sparkles size={13} className="animate-pulse" /> : <AlertOctagon size={13} />}
              label={briefLoading ? "Drafting brief…" : "Escalate to founder"}
              onClick={openEscalateToFounder}
              disabled={briefLoading}
              testid={`triage-escalate-${esc.id}`}
            />
            <ActionButton
              icon={<MessageSquare size={13} />}
              label="Reach out"
              onClick={openReachOut}
              disabled={linkedPeople.length === 0}
              testid={`triage-reachout-${esc.id}`}
            />
          </div>
          <p className="text-[11px] text-neutral-500">
            <strong className="text-neutral-700">Escalate to founder</strong> drafts an email with AI, opens the reach-out popup. Sending it saves the message under <em>Messages · Sent</em>. Cancelling saves it under <em>Drafts</em>.
          </p>
        </div>
      )}

      {reachOutOpen && reachOutInitial && (
        <ReachOutModal
          initial={reachOutInitial}
          people={people}
          onClose={() => { setReachOutOpen(false); setReachOutInitial(null); }}
          onSent={() => { setReachOutOpen(false); setReachOutInitial(null); setStatus("resolved"); }}
        />
      )}
    </div>
  );
}

function ActionButton({ icon, label, onClick, testid, variant, disabled }) {
  const cls = variant === "primary"
    ? "bg-neutral-900 text-white hover:bg-neutral-800 border border-neutral-900"
    : "bg-white text-blue-600 hover:bg-neutral-50 border border-neutral-200 font-semibold";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 justify-center text-xs px-3 py-2 rounded-sm ${cls} transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {icon}{label}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Activity feed row — with hover-revealed popover menu
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Activity feed row — with hover-revealed popover menu
// -----------------------------------------------------------------------------
function EventRow({ event, people, projects, focused }) {
  const nav = useNavigate();
  const { openPerson: openPersonPanel } = usePersonPanel();
  const person = people.find((p) => (event.people_ids || []).includes(p.id));
  const project = projects.find((p) => (event.project_ids || []).includes(p.id));
  const critical = (event.urgency_score || 0) >= 4;
  const warning = (event.urgency_score || 0) === 3;

  const openPerson = () => { if (person) openPersonPanel(person.id); };
  const openProject = () => { if (project) nav(`/projects?focus=${project.id}`); };
  const openInbox = () => nav(`/inbox?focus=${event.id}`);

  const reachOut = async () => {
    if (!person) return;
    try {
      await endpoints.draftMessage(person.id, "follow_up");
      toast.success(`Draft ready for ${person.name}`);
      nav(`/people?focus=${person.id}`);
    } catch (e) { toast.error("Draft failed"); }
  };

  const escalate = async () => {
    try {
      await endpoints.inboxEscalate({ event_id: event.id, reason: event.content_summary, urgency: 4 });
      toast.success("Escalation created");
    } catch (e) { toast.error("Could not escalate"); }
  };

  const archive = async () => {
    try {
      await endpoints.inboxArchive(event.id);
      toast.success("Archived");
    } catch (e) { toast.error("Could not archive"); }
  };

  const markerCls = critical ? "critical-marker" : warning ? "warning-marker" : "border-l-2 border-neutral-200";

  const actions = [
    { icon: <InboxIconSmall size={14} />, label: "Triage in inbox", onClick: openInbox, testid: `event-triage-${event.id}` },
    project && { icon: <TargetIconSmall size={14} />, label: `Open project · ${project.name}`, onClick: openProject, testid: `event-project-${event.id}` },
    person && { icon: <UserIconSmall size={14} />, label: `Open person · ${person.name}`, onClick: openPerson, testid: `event-person-${event.id}` },
    person && { icon: <MessageSquare size={14} />, label: `Reach out to ${person.name}`, onClick: reachOut, testid: `event-reachout-${event.id}` },
    { icon: <AlertOctagon size={14} />, label: "Escalate to founder", onClick: escalate, testid: `event-escalate-${event.id}` },
    { icon: <Archive size={14} />, label: "Archive", onClick: archive, testid: `event-archive-${event.id}` },
  ];

  return (
    <div
      className={`fos-card group p-3 pr-4 flex items-start gap-3 ${markerCls} ${focused ? "highlighted-entity" : ""}`}
      data-testid={`event-${event.id}`}
      data-focus-id={event.id}
    >
      {person ? <Avatar person={person} /> : <ChannelIcon channel={event.source_channel} size={20} className="mt-1" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug text-neutral-900">{event.content_summary}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <ChannelIcon channel={event.source_channel} size={11} />
          <span className="text-[11px] text-neutral-500 capitalize">{event.classification || "fyi"}</span>
          {critical && <span className="text-[11px] text-[#FF453A]">Urgency {event.urgency_score}/5</span>}
          <span className="text-[11px] text-neutral-400">·</span>
          <span className="text-[11px] text-neutral-500">{relTime(event.created_at)}</span>
          {project && (
            <button onClick={openProject} className="text-[11px] text-neutral-700 underline decoration-neutral-300 hover:decoration-neutral-900">
              {project.name}
            </button>
          )}
          {person && (
            <PersonLink id={person.id} prefix="@" className="text-[11px]" />
          )}
        </div>
      </div>
      <ActionPopover items={actions} testid={`event-actions-${event.id}`} />
    </div>
  );
}

// Small lucide-icon wrappers keep JSX above readable (unused now, kept for backwards refs).
function InboxAction() { return <InboxIconSmall size={13} />; }
function TargetAction() { return <TargetIconSmall size={13} />; }
function UserAction() { return <UserIconSmall size={13} />; }

function Avatar({ person }) {
  const src = person.avatar_url || gravatarUrl(person.email, 48);
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={person.name}
        onError={() => setFailed(true)}
        className="h-9 w-9 rounded-full object-cover shrink-0 border border-neutral-200"
      />
    );
  }
  return (
    <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 bg-neutral-200 text-neutral-800">
      {initials(person.name)}
    </div>
  );
}
